// Working hours, time off and holidays in Settings (BL-014 phase 2), kept apart from the screens so
// they are tested: who may change what, a person's week as a form and back, and the words.

/** One window of a working day: weekday 1 = Monday, times HH:MM in the company's time. */
export interface HoursWindow {
  day: number;
  start: string;
  end: string;
}

/** staff.hours */
export interface StaffHours {
  staffId: string;
  from: string | null;
  windows: HoursWindow[] | null;
  company: { open: string; close: string; days: number[] };
  history: { from: string; windows: HoursWindow[] | null }[];
}

/** timeOff.list's row */
export interface TimeOff {
  id: string;
  staffId: string;
  staffName: string;
  code: string;
  label: string;
  paid: boolean;
  start: string;
  end: string;
  allDay: boolean;
  note: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  text: string;
}

export interface Holiday {
  date: string;
  name: string;
  paid: boolean;
}

export interface NonJobCode {
  code: string;
  name: string;
  category: 'activity' | 'leave';
  paid: boolean;
  countsTowardOvertime: boolean;
  active: boolean;
}

/** Who may change what (as built: staff.hours.manage; nonJobCodes.save is settings.edit). */
export function hoursRights(held: readonly string[]) {
  const has = (p: string) => held.includes(p);
  return {
    /** working hours, time off and holidays (staff.hours.manage) */
    manage: has('staff.hours.manage'),
    /** the non-job codes (settings.edit) */
    codes: has('settings.edit'),
  };
}

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** A week as the form edits it: each day's windows, in order. */
export type WeekForm = { day: number; windows: { start: string; end: string }[] }[];

/** The form for a person's hours: their own windows, or (none set) the company's as a start. */
export function weekForm(h: Pick<StaffHours, 'windows' | 'company'>): WeekForm {
  return DAYS.map((_, i) => {
    const day = i + 1;
    if (h.windows)
      return {
        day,
        windows: h.windows
          .filter((w) => w.day === day)
          .sort((a, b) => a.start.localeCompare(b.start))
          .map((w) => ({ start: w.start, end: w.end })),
      };
    return {
      day,
      windows: h.company.days.includes(day) ? [{ start: h.company.open, end: h.company.close }] : [],
    };
  });
}

/** What the form sends (staff.setHours' windows), and what's wrong with it first, in words. */
export function weekWindows(f: WeekForm): { windows: HoursWindow[] } | { problem: string } {
  const out: HoursWindow[] = [];
  for (const d of f) {
    const sorted = [...d.windows].sort((a, b) => a.start.localeCompare(b.start));
    for (const [i, w] of sorted.entries()) {
      if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(w.start) || !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(w.end))
        return { problem: `${DAYS[d.day - 1]}: a time like 08:00.` };
      if (w.end <= w.start) return { problem: `${DAYS[d.day - 1]}: it ends after it starts.` };
      const next = sorted[i + 1];
      if (next && next.start < w.end) return { problem: `${DAYS[d.day - 1]}: two windows overlap.` };
      out.push({ day: d.day, start: w.start, end: w.end });
    }
  }
  return { windows: out };
}

/** 13:30 → "1:30 p.m.", 08:00 → "8 a.m." */
export function clockText(hm: string): string {
  const [h, m] = hm.split(':').map(Number) as [number, number];
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'a.m.' : 'p.m.'}`;
}

const spanText = (w: { start: string; end: string }) => `${clockText(w.start)}–${clockText(w.end)}`;

/**
 * A week in words, runs of days with the same hours joined: "Mon–Fri 8 a.m.–4:30 p.m.; Sat 9 a.m.–1 p.m.".
 * Days off are left out; none worked at all: "No working days".
 */
export function weekText(windows: readonly HoursWindow[]): string {
  const of = (day: number) =>
    windows
      .filter((w) => w.day === day)
      .sort((a, b) => a.start.localeCompare(b.start))
      .map(spanText)
      .join(', ');
  const runs: { from: number; to: number; text: string }[] = [];
  for (let day = 1; day <= 7; day++) {
    const text = of(day);
    if (!text) continue;
    const last = runs[runs.length - 1];
    if (last && last.to === day - 1 && last.text === text) last.to = day;
    else runs.push({ from: day, to: day, text });
  }
  if (!runs.length) return 'No working days';
  return runs
    .map((r) => `${SHORT[r.from - 1]}${r.to > r.from ? `–${SHORT[r.to - 1]}` : ''} ${r.text}`)
    .join('; ');
}

/** What a person works: their own week, or "The company's hours (Mon–Fri 8 a.m.–5 p.m.)". */
export function hoursText(h: Pick<StaffHours, 'windows' | 'company'>): string {
  if (h.windows) return weekText(h.windows);
  const c = h.company;
  return `The company’s hours (${weekText(c.days.map((day) => ({ day, start: c.open, end: c.close })))})`;
}

/** The time-off form: whole days from one date to another, or part of one day. */
export interface TimeOffForm {
  code: string;
  partDay: boolean;
  from: string;
  to: string;
  start: string;
  end: string;
  note: string;
}

/** What timeOff.add takes, or what's missing, in words. */
export function timeOffInput(
  staffId: string,
  f: TimeOffForm,
): { input: Record<string, unknown> } | { problem: string } {
  if (!f.code) return { problem: 'Pick what kind of time off.' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.from)) return { problem: 'Pick the day it starts.' };
  const note = f.note.trim() ? { note: f.note.trim() } : {};
  if (f.partDay) {
    if (!f.start || !f.end) return { problem: 'A part day needs a start and an end time.' };
    if (f.end <= f.start) return { problem: 'It ends after it starts.' };
    return { input: { staffId, code: f.code, from: f.from, start: f.start, end: f.end, ...note } };
  }
  const to = f.to || f.from;
  if (to < f.from) return { problem: 'It ends on or after the day it starts.' };
  return { input: { staffId, code: f.code, from: f.from, to, ...note } };
}

/** The code a new non-job code gets from its name: "Shop time" → "shop_time". */
export const codeOf = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'c_$1')
    .slice(0, 30) || 'code';

/** A code's terms in words: "Leave · paid · not toward overtime". */
export function codeTerms(
  c: Pick<NonJobCode, 'category' | 'paid' | 'countsTowardOvertime' | 'active'>,
): string {
  return [
    c.category === 'leave' ? 'Leave the office books' : 'Started by the technician',
    c.paid ? 'paid' : 'unpaid',
    c.countsTowardOvertime ? 'toward overtime' : 'not toward overtime',
    ...(c.active ? [] : ['retired']),
  ].join(' · ');
}
